import { Icon } from '@iconify/react'
import { Spin } from 'antd'
import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import type { AvailabilityKind, AvailabilitySeries } from 'features/Schedule/api/availabilitySeries.types'
import {
  canRetrySeries,
  rowLock,
  seriesRunsLabel,
  seriesTimeLabel,
  syncLabel,
} from './availabilitySeries.utils'
import styles from './doctorSchedule.module.scss'

type DoctorScheduleColumnsParams = {
  kinds: AvailabilityKind[]
  rows: AvailabilitySeries[]
  retrying: boolean
  discarding: boolean
  deletingId: number | null
  onRetry: (id: number) => void
  onDiscard: (id: number) => void
  onDelete: (id: number) => void
}

export const getDoctorScheduleColumns = ({
  kinds,
  rows,
  retrying,
  discarding,
  deletingId,
  onRetry,
  onDiscard,
  onDelete,
}: DoctorScheduleColumnsParams) => [
  {
    title: 'Kind',
    dataIndex: 'kind',
    key: 'kind',
    render: (_: AvailabilitySeries['kind'], row: AvailabilitySeries) => (
      <div className={styles.kindCell}>
        <span className={styles.dot} style={{ background: kinds.find((kind) => kind.id === row.kind.id)?.color }} />
        <div>
          <div className={styles.kindName}>{row.kind.name}</div>
          {row.title && row.title !== row.kind.name ? <div className={styles.kindTitle}>{row.title}</div> : null}
          {row.comment ? (
            <div className={styles.kindComment} title={row.comment}>
              {row.comment}
            </div>
          ) : null}
          {syncLabel(row) ? (
            <div className={row.sync_status === 'sync_failed' ? styles.syncFailed : styles.sync}>
              {syncLabel(row)}
            </div>
          ) : null}
        </div>
      </div>
    ),
  },
  { title: 'Repeats', dataIndex: 'repeat_text', key: 'repeats' },
  {
    title: 'Time',
    dataIndex: 'start_time',
    key: 'time',
    render: (_: string, row: AvailabilitySeries) => seriesTimeLabel(row),
  },
  {
    title: 'Runs',
    dataIndex: 'start_date',
    key: 'runs',
    render: (_: string, row: AvailabilitySeries) => seriesRunsLabel(row),
  },
  {
    title: 'Actions',
    dataIndex: 'id',
    key: 'actions',
    width: 72,
    align: 'center' as const,
    className: styles.actionsCell,
    render: (_: number, row: AvailabilitySeries) => {
      const lock = rowLock(row, rows)
      return (
        <div className={styles.actions} onClick={(event) => event.stopPropagation()}>
          {canRetrySeries(row) ? (
            <>
              <Button
                modifier={BUTTON_MODIFIER.SECONDARY}
                proportion={BUTTON_PROPORTION.SMALL}
                disabled={retrying}
                onClick={() => onRetry(row.id)}>
                Retry
              </Button>
              <Button
                modifier={BUTTON_MODIFIER.SECONDARY}
                proportion={BUTTON_PROPORTION.SMALL}
                disabled={discarding}
                onClick={() => onDiscard(row.id)}>
                Discard
              </Button>
            </>
          ) : null}
          {deletingId === row.id ? (
            <span className={styles.deleting} aria-label='Deleting block'>
              <Spin size='small' />
            </span>
          ) : lock === 'open' || lock === 'unknown' ? (
            <button
              type='button'
              className={styles.trash}
              aria-label='Delete block'
              onClick={() => onDelete(row.id)}>
              <Icon icon='mdi:trash-can-outline' width={18} height={18} />
            </button>
          ) : null}
        </div>
      )
    },
  },
]
